import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { money } from '../../lib/money';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  FilterBar,
  PageContainer,
  PageHeader,
  Pagination,
  Spinner,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { requisitionPath } from '../../lib/slug';
import { buildFilterParams } from '../../lib/filters';
import { catalogApi } from '../catalog/catalogApi';
import { requisitionsApi, reqStatusMeta, reqPriorityMeta, REQ_STATUSES, REQ_PRIORITIES } from './requisitionsApi';
import './requisitions.css';

const PAGE_SIZE = 25;

export default function RequisitionsPage() {
  const navigate = useNavigate();
  const t = useT();
  const { hasPermission, activePropertyId, properties } = useProperty();
  const canCreate = hasPermission('requisitions.create');

  const [data, setData] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [values, setValues] = useState({});
  const [mineOnly, setMineOnly] = useState(false);
  const [locations, setLocations] = useState([]);
  const [page, setPage] = useState(1);
  const debounceRef = useRef(null);

  useEffect(() => {
    let active = true;
    catalogApi.listLocations(activePropertyId).then((res) => { if (active) setLocations(res || []); }).catch(() => {});
    return () => { active = false; };
  }, [activePropertyId]);

  const fields = useMemo(() => [
    { id: 'statuses', label: t('Status'), type: 'multiselect', options: REQ_STATUSES.map((s) => ({ value: s.value, label: t(s.label) })) },
    { id: 'priorities', label: t('Priority'), type: 'multiselect', options: REQ_PRIORITIES.map((p) => ({ value: p.value, label: t(p.label) })) },
    { id: 'locationIds', label: t('Location'), type: 'multiselect', searchable: true, options: locations.map((l) => ({ value: l.id, label: l.name })) },
    { id: 'total', label: t('Est. total'), type: 'numberrange', minParam: 'totalMin', maxParam: 'totalMax' },
    { id: 'needed', label: t('Needed by'), type: 'daterange', fromParam: 'neededFrom', toParam: 'neededTo' },
    { id: 'created', label: t('Created'), type: 'daterange', fromParam: 'createdFrom', toParam: 'createdTo' },
  ], [locations, t]);

  const filterParams = useMemo(() => buildFilterParams(fields, values), [fields, values]);
  const hasFilters = search !== '' || mineOnly || Object.keys(filterParams).length > 0;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await requisitionsApi.list({ page, pageSize: PAGE_SIZE, search, mineOnly: mineOnly || undefined, ...filterParams });
      setData(res);
    } catch (err) {
      setError(err?.message || t('Failed to load requisitions.'));
    } finally {
      setLoading(false);
    }
  }, [page, search, mineOnly, filterParams, t]);

  useEffect(() => { load(); }, [load]);

  const onSearchChange = (value) => {
    setSearchText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); setSearch(value); }, 300);
  };

  const onFilterChange = (id, value) => {
    setPage(1);
    setValues((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[id];
      else next[id] = value;
      return next;
    });
  };

  const open = (row) => navigate(requisitionPath(row));

  const propertyName = (id) => properties.find((p) => p.id === id)?.name;

  const columns = [
    {
      key: 'number', header: t('Number'), width: 130,
      render: (r) => <span style={{ fontFamily: 'var(--font-mono)' }}>{r.number}</span>,
    },
    // In "All properties" view, show which property each requisition belongs to.
    ...(!activePropertyId ? [{
      key: 'property', header: t('Property'), width: 160,
      render: (r) => propertyName(r.propertyId) || <span className="pp-cell-muted">—</span>,
    }] : []),
    {
      key: 'title', header: t('Title'), width: 280,
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.title}</div>
          <div className="pp-cell-muted" style={{ fontSize: 'var(--text-xs)' }}>{r.requestedByName || '—'}</div>
        </div>
      ),
    },
    {
      key: 'status', header: t('Status'), width: 150,
      render: (r) => { const m = reqStatusMeta(r.status); return <Badge tone={m.tone} dot>{t(m.label)}</Badge>; },
    },
    {
      key: 'priority', header: t('Priority'), width: 110,
      render: (r) => { const m = reqPriorityMeta(r.priority); return <Badge tone={m.tone}>{t(m.label)}</Badge>; },
    },
    { key: 'lines', header: t('Lines'), width: 80, align: 'right', render: (r) => r.lineCount },
    { key: 'total', header: t('Est. total'), width: 140, align: 'right', render: (r) => money(r.estimatedTotal) },
    {
      key: 'needed', header: t('Needed by'), width: 130,
      render: (r) => (r.neededByDate ? new Date(r.neededByDate).toLocaleDateString() : <span className="pp-cell-muted">—</span>),
    },
  ];

  const totalPages = Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE));

  return (
    <PageContainer size="full">
      <PageHeader
        title={t('Requisitions')}
        description={t('Purchase requests raised against your properties, routed for approval.')}
        icon={<Icon.ListChecks size={22} />}
        actions={canCreate ? (
          <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={() => navigate('/requisitions/new')}>
            {t('New requisition')}
          </Button>
        ) : null}
      />

      <FilterBar
        searchPlaceholder={t('Search by number, title or requester…')}
        searchValue={searchText}
        onSearchChange={onSearchChange}
        fields={fields}
        values={values}
        onChange={onFilterChange}
      >
        <Button variant={mineOnly ? 'primary' : 'outline'} size="sm" onClick={() => { setPage(1); setMineOnly((v) => !v); }}>
          {mineOnly ? t('My requisitions') : t('All requisitions')}
        </Button>
      </FilterBar>

      {error ? (
        <EmptyState
          icon={<Icon.CircleAlert size={28} />}
          title={t("Couldn't load requisitions")}
          description={error}
          action={<Button variant="secondary" onClick={load}>{t('Retry')}</Button>}
        />
      ) : loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={data.items}
            rowKey={(r) => r.id}
            density="comfortable"
            onRowClick={open}
            emptyState={
              <EmptyState
                icon={<Icon.ListChecks size={28} />}
                title={hasFilters ? t('No matching requisitions') : t('No requisitions yet')}
                description={hasFilters ? t('Try adjusting your search or filters.') : t('Raise your first purchase requisition to get goods approved and ordered.')}
                action={!hasFilters && canCreate ? (
                  <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={() => navigate('/requisitions/new')}>{t('New requisition')}</Button>
                ) : null}
              />
            }
          />
          {data.totalCount > 0 && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <Pagination page={page} pageCount={totalPages} onPageChange={setPage} totalItems={data.totalCount} pageSize={PAGE_SIZE} />
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}
